<template>
	<header>
		<nav class="container flex flex-col p-4 pt-2 pb-0">
			<div class="flex justify-between items-start">
				<nav 
					v-if="!isHome && !isRequest && !isGdpr && !isHelpcenter" 
					class="container pt-2 pb-3 pl-0 text-sp-tertiary"
				>
					<ul class="breadcrumbs flex flex-wrap items-center gap-2">
						<TransitionGroup name="fade" tag="div" class="flex flex-wrap items-center gap-2">
							<li
								v-for="(crumb, index) in breadcrumbs"
								:key="index"
								class="flex items-center whitespace-nowrap"
							>
								<RouterLink
									v-if="crumb.link"
									:to="withQuery(crumb.link)"
									@click="logClick(`header.${crumb.link.replace(/(category|section|article)\//g, '').replace(/^\/+/, '') || 'home'}`, 'header.breadCrumbs')"
									class="underline underline-offset-2 hover:text-sp-accent duration-150 ease-in-out"
								>
									{{ crumb.label }}
								</RouterLink>
								<span v-else>{{ crumb.label }}</span>

								<span v-if="index < breadcrumbs.length - 1" class>
									<img src="../assets/icon-arrow.png" alt=">" class="w-2.5 inline-block ml-2" />
								</span>
							</li>
						</TransitionGroup>
					</ul>
				</nav>

				<div v-if="isHome || isHelpcenter" class="h-9 w-9 min-w-9"></div>

				<nav v-if="isRequest || isGdpr" class="pt-2 pb-3 text-sp-tertiary">
					<RouterLink 
						to="/"
						@click="logClick('header.home', 'header.breadCrumbs')"
						class="underline underline-offset-2 hover:text-sp-accent duration-150 ease-in-out"
					>
						{{ $t("homePage") }}
					</RouterLink>
				</nav>

				<div v-if="isHome || isHelpcenter">
					<h1 class="heading text-2xl text-sp-accent text-center font-bold">
						Support Portal
					</h1>
				</div>

				<div class="h-9 w-9 min-w-9">
					<button v-if="!isBrowser" @click="closeSp" class="header-button h-9 w-9">
						<img src="../assets/close-button.png" alt="Close button">
					</button>
				</div>
			</div>
		</nav>
	</header>

	<RouterView />
</template>


<script setup>
import { computed, TransitionGroup } from 'vue'
import { useRoute } from 'vue-router'
import { useHelpCenterStore } from '../stores/helpCenter'
import { useLogger } from '../composables/useLogger'
import { useTranslate } from '../composables/useTranslate'
import { useBrowserMode } from '../composables/useBrowserMode'
import { UNIWEBVIEW_CLOSE_URL } from '../config/integrations'

const route = useRoute()
const helpCenter = useHelpCenterStore()
const { getTranslation } = useTranslate()
const { logClick } = useLogger()
const { isBrowser } = useBrowserMode()

const isHome = computed(() => route.path === '/')
const isRequest = computed(() => route.path === '/request')
const isGdpr = computed(() => route.path === '/gdpr')
const isHelpcenter = computed(() => route.path === '/helpcenter')

function withQuery(to) {
  return {
    path: to,
    query: route.query
  }
}

const breadcrumbs = computed(() => {
  const crumbs = [{ label: getTranslation('homePage'), link: '/' }]

  const routeCategoryId = route.params.categoryId
  const routeSectionId = route.params.sectionId
  const routeArticleId = route.params.articleId

  const storedCategory = helpCenter.currentCategory
  const storedSection = helpCenter.currentSection
  const storedArticle = helpCenter.currentArticle

  const labelFor = (stored, routeId) => {
    if (stored?.id != null && String(stored.id) === String(routeId)) {
      return stored.name || '...'
    }
    return '...'
  }

  if (routeCategoryId) {
    crumbs.push({
      label: labelFor(storedCategory, routeCategoryId),
      link: `/category/${routeCategoryId}`
    })
  }

  if (routeSectionId) {
    crumbs.push({
      label: labelFor(storedSection, routeSectionId),
      link: `/category/${routeCategoryId}/section/${routeSectionId}`
    })
  }

  if (routeArticleId) {
    crumbs.push({
      label: labelFor(storedArticle, routeArticleId),
      link: null
    })
  }

  return crumbs
})

function closeSp() {
	logClick('header.closeSp', 'header.closeSp')
	window.location.href = UNIWEBVIEW_CLOSE_URL
}

</script>

<style>
.heading {
	position: relative;
  padding: 0 2.5rem 0.75rem;
  background-image: 
		url('../assets/header-triangle.png'),
    url('../assets/header-line.png');
  background-repeat: no-repeat, no-repeat;
	background-position: center calc(100% - 0.3rem), center calc(100% - 0.75rem);
  background-size: auto 0.5rem, 100% 1.25rem;	
}
.breadcrumbs li:last-of-type {
  color: var(--sp-accent);
	text-decoration: underline;
	text-underline-offset: 2px;
}
.breadcrumbs li:last-of-type a {
  cursor: default;
}
</style>